<script>
import Modal from './Modal.vue';
import PricingBot from '../pricing_bot/PricingBot.vue';
import PricingBotRating from '../pricing_bot/PricingBotRating.vue';
import TextAreaInput from '../TextAreaInput.vue';

export default {
  components: {PricingBot, Modal, TextAreaInput, PricingBotRating},
  props: {
    id: { type: String, required: true },
    data: { type: Object, required: true },
  },
  data() {
    return {
      message: '',
      sessionId: null,
      showHelpPicker: true,
      showMessageContainsMobileApp: false,
      showInfoMessage: true,
      showRatingElement: false,
      selectedRating: null,
    };
  },
  created() {
    if (!this.isSessionStorageEnabled()) {
      return;
    } else if (
      sessionStorage.getItem('wasMessageInfoClicked') === 'true' ||
      sessionStorage.getItem('lastConversation')
    ) {
      this.showInfoMessage = false;
    }
  },
  mounted() {
    this.checkMessageInput();

    this.$nextTick(() => {
      this.$refs.modal?.scrollToBottom();
    });
  },
  methods: {
    onMessageSend() {
      this.hideMessageInfoBox();
      this.$refs.pricingBot?.onMessageSend(this.message);
      this.message = '';

      setTimeout(() => {
        this.$refs.modal?.scrollToBottom();
      }, 250);
    },
    onNewMessage() {
      setTimeout(() => {
        this.$refs.modal?.scrollToBottom();
      }, 250);
    },

    onSessionIdUpdate(sessionId) {
      this.sessionId = sessionId;
    },
    onTextAreaKeyPress(key) {
      if (!this.message || this.message.trim() === '') {
        return;
      }

      if (key === 'enter') {
        this.$refs.messageInput?.blur();
        this.onMessageSend();
      }
    },
    checkMessageInput() {
      const input = this.$el.querySelector('#message');
      let previousValue = input.value;
      let initialCheck = true;

      const checkValue = () => {
        const currentValue = input.value;
        if (initialCheck || currentValue !== previousValue) {
          this.message = currentValue;
          previousValue = currentValue;
          initialCheck = false;
          setTimeout(checkValue, 200);
        }
      };

      checkValue();
    },
    handleClickOurMobileApp() {
      this.showMessageContainsMobileApp = true;
      this.showInfoMessage = false;
      if (this.isSessionStorageEnabled()) {
        sessionStorage.setItem('wasMessageInfoClicked', 'true');
      }
    },
    hideMessageInfoBox() {
      this.showInfoMessage = false;
    },
    isSessionStorageEnabled() {
      try {
        const key = `__storage__test`;
        window.sessionStorage.setItem(key, null);
        window.sessionStorage.removeItem(key);
        return true;
      } catch (e) {
        return false;
      }
    },
    openRating() {
      this.showRatingElement = true;
    },
    closeModal() {
      this.$emit('close', this.id);
    },
    requestClose() {
      const washyRatedAt = this.isSessionStorageEnabled() ? Number(sessionStorage.getItem('washyRatedAt') || '0') : '0';
      const messagesLength = this.$refs.pricingBot?.messages.length || 0;

      if (this.showRatingElement || !messagesLength || washyRatedAt === messagesLength) {
        this.closeModal();
        return;
      }
      this.openRating();
    },
    onRating(option) {
      this.selectedRating = option;

      // Store the number of messages at the time of rating to avoid showing the rating pop-up again
      // if the user has already rated and continues the conversation
      if(this.isSessionStorageEnabled()){
        sessionStorage.setItem('washyRatedAt', String(this.$refs.pricingBot?.messages.length || 0));
      }

      fetch(window.washyRatingUrl, {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        method: 'POST',
        body: JSON.stringify({
          chatUuid: this.sessionId,
          rating: this.selectedRating,
        }),
      }).catch(e => {
        // "Failed to fetch" means it lost connection while loading or closed modal before it loaded,
        // so we can ignore that error.
        if (window.Sentry && e.message !== 'Failed to fetch') {
          window.Sentry.captureException(e);
        }
      });

      setTimeout(() => {
        this.closeModal();
      }, 200);
    },
  },
};
</script>

<template>
  <Modal
    :id="id"
    ref="modal"
    :on-close="requestClose"
    disable-backdrop-close
    hide-scrollbar
    no-padding-footer
  >
    <template #head>
      <p class="text-center">
        {{ $t('bot.title') }}
      </p>
    </template>
    <PricingBot
      ref="pricingBot"
      v-model="message"
      :session-id="sessionId"
      :onClose="() => $emit('close', id)"
      :show-message-contains-mobile-app="showMessageContainsMobileApp"
      @newMessage="onNewMessage"
      @sessionIdUpdate="onSessionIdUpdate"
    />

    <template #foot class="bot-modal-bottom modal-footer">
      <div v-if="showRatingElement">
        <PricingBotRating :selected-option="selectedRating" @option-selected="onRating"/>
      </div>
      <div
        v-if="showInfoMessage"
        class="pricing-bot-info-box bg-background-grey py-8 px-16 d-flex align-items-center gap-8"
      >
        <img
          :src="require('images/price_bot/light-bulb.svg')"
          :alt="$t('bot.upsell.image_alt')"
          height="24"
          width="24"
        />
        <span
          >{{ $t('bot.forExistingOrders') }}
          <a @click.prevent.stop="handleClickOurMobileApp" class="text-underline clickable">{{
            $t('bot.ourMobileApp')
          }}</a></span
        >

        <img
          :src="require('images/price_bot/closeGrey.svg')"
          class="ms-auto clickable"
          :alt="$t('bot.upsell.image_alt')"
          @click="hideMessageInfoBox"
          height="12"
          width="12"
        />
      </div>
      <div class="pricing-bot-message-wrapper">
        <TextAreaInput
          v-model="message"
          name="message"
          class="d-block pricing-bot-text-input pe-48"
          ref="messageInput"
          :label="''"
          :placeholder="$t('bot.askUsAnything')"
          :singleLineScroll="true"
          @keyUp="onTextAreaKeyPress"
        />

        <img
          :src="
            !message || message.trim() === ''
              ? require('images/price_bot/chatButtonDisabled.svg')
              : require('images/price_bot/chatButton.svg')
          "
          class="pricing-bot-message-button button p-0"
          :class="{ disabled: !message }"
          width="32"
          height="32"
          @click="onMessageSend"
        />
      </div>
    </template>
  </Modal>
</template>
